import React, {useState, useEffect} from 'react';
import {View} from 'react-native';
import {Divider} from 'react-native-paper';
import moment from 'moment';
import isFinite from 'lodash/isFinite';

import {Pressable, Dialog, Text, Link} from '~/components/controls';
import Format from '~/lib/format';

import PriceCheckIcon from '~/images/md-icons/price_check/materialicons/24px.svg';
import EuroSymbolIcon from '~/images/md-icons/euro_symbol/materialicons/24px.svg';
import TimerIcon from '~/images/md-icons/timer/materialicons/24px.svg';

import sy from '~/styles';
import PropTypes from 'prop-types';

const TYPES = {
  Green: 'green',
  Orange: 'orange',
  Red: 'red',
};

const getTypeColor = (value) => {
  if (value === TYPES.Green) {
    return '#299976';
  }

  if (value === TYPES.Red) {
    return '#972727';
  }

  return '#FF6C00';
};

const getRangeDate = (type, range) => {
  if ([TYPES.Green, TYPES.Orange].includes(type)) {
    return range?.max_date;
  }

  return range?.min_date;
};

const getRangeLabel = (type) => {
  if (type === TYPES.Green) {
    return 'gelijk of hoger bedrag goedgekeurd';
  }

  if (type === TYPES.Orange) {
    return 'lager bedrag goedgekeurd';
  }

  return 'lager bedrag afgekeurd';
};

const PriceCheck = ({
  rob_code,
  amount,
  range,
  duration,
  suggested_duration,
  onChangeDuration,
}) => {
  const [type, setType] = useState(null);
  const [durationType, setDurationType] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (range?.min && range?.max) {
      if (amount <= range.min) {
        setType(TYPES.Green);
      } else if (range.min < range.max && amount > range.max) {
        setType(TYPES.Red);
      } else {
        setType(TYPES.Orange);
      }
    } else {
      setType(null);
    }
  }, [amount, range]);

  useEffect(() => {
    if (isFinite(duration) && isFinite(suggested_duration)) {
      const rounded_duration = Format.round(duration);
      const rounded_suggested_duration = Format.round(suggested_duration);
      if (rounded_duration > rounded_suggested_duration) {
        setDurationType(TYPES.Red);
      } else {
        setDurationType(TYPES.Green);
      }
    } else {
      setDurationType(null);
    }
  }, [duration, suggested_duration]);

  return (
    <>
      <Pressable onPress={() => setShowDialog((prevState) => !prevState)}>
        <PriceCheckIcon fill={getTypeColor(type ?? durationType)} />
      </Pressable>
      <Dialog
        title={`Prijscheck ${rob_code ?? ''}`.trim()}
        titleIcon={<PriceCheckIcon fill={getTypeColor(type ?? durationType)} />}
        visible={showDialog}
        onDismiss={() => setShowDialog(false)}
        options={{
          noPaddingContent: true,
        }}>
        {durationType != null && (
          <>
            <View style={[sy['p-4'], sy['gap-8'], sy['flex-row']]}>
              <TimerIcon fill={getTypeColor(durationType)} />
              <View style={[sy['flex-1']]}>
                <Text>
                  Tijd {durationType === TYPES.Green ? <>OK</> : <>te hoog?</>}
                </Text>
                <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                  <>{Format.number(suggested_duration, 2)}</>&nbsp;uur meest
                  goedgekeurd
                </Text>
                {durationType !== TYPES.Green && (
                  <Link
                    textStyle={[
                      sy.smallRegular,
                      sy['no-underline'],
                      {color: '#2907E3'},
                    ]}
                    onPress={() => {
                      onChangeDuration(suggested_duration, duration);
                      setShowDialog(false);
                    }}>
                    Toepassen
                  </Link>
                )}
              </View>
            </View>
            <Divider style={sy['bg-gray88']} />
          </>
        )}
        {type != null && (
          <View style={[sy['p-4'], sy['gap-8'], sy['flex-row']]}>
            <EuroSymbolIcon fill={getTypeColor(type)} />
            <View style={[sy['flex-1']]}>
              <Text>
                Bedrag {type === TYPES.Green ? <>OK</> : <>te hoog?</>}
              </Text>
              <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                Op&nbsp;
                {moment(getRangeDate(type, range)).format('D MMMM YYYY')}
                &nbsp;met&nbsp;
                {getRangeLabel(type)}
              </Text>
            </View>
          </View>
        )}
      </Dialog>
    </>
  );
};
PriceCheck.propTypes = {
  rob_code: PropTypes.any,
  amount: PropTypes.any,
  range: PropTypes.any,
  duration: PropTypes.any,
  suggested_duration: PropTypes.any,
  onChangeDuration: PropTypes.any,
};

export default PriceCheck;
